import React, { useEffect, useState } from 'react';
import './css/Footer.css';
import { MapPin, Phone, Mail, Link } from 'lucide-react';
import { DynamicIcon } from 'lucide-react/dynamic';
import axios from 'axios';
import { lucideIconForFooterPlatform } from '../constants/footerSocialPlatforms';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

const defaultFooter = {
  brandName: 'Whales Advisory',
  firmDescription:
    'Delivering refined, solution-driven legal support to start-ups, SMEs, and large corporates in the Egyptian market.',
  focusHeading: 'Our Focus',
  focusItems: [
    'Corporate Advisory',
    'Digital Platform Services',
    'International & Regional Firms',
  ],
  contactHeading: 'Contact',
  address: 'Sheikh Zaid, Giza, Egypt',
  phone: '01000355950',
  email: 'Info@WhalesAdvisory.com',
  copyrightText: 'Copyright © 2026 Whales Advisory. All Rights Reserved.',
  socialLinks: [],
};

function normalizeSocialLinksFromApi(data) {
  if (Array.isArray(data.socialLinks) && data.socialLinks.length > 0) {
    return data.socialLinks.map((item) => ({
      platform: String(item.platform || '').trim().toLowerCase(),
      url: String(item.url || '').trim(),
    }));
  }
  const legacy = [];
  if (data.linkedinUrl && String(data.linkedinUrl).trim()) {
    legacy.push({ platform: 'linkedin', url: String(data.linkedinUrl).trim() });
  }
  if (data.facebookUrl && String(data.facebookUrl).trim()) {
    legacy.push({ platform: 'facebook', url: String(data.facebookUrl).trim() });
  }
  return legacy;
}

function hasDisplayableSocialUrl(url) {
  if (url == null) return false;
  const t = String(url).trim();
  if (!t) return false;
  if (t === '#' || t === '/') return false;
  if (/^javascript:/i.test(t)) return false;
  return true;
}

function socialAriaLabel(platform) {
  const labels = {
    linkedin: 'LinkedIn',
    facebook: 'Facebook',
    instagram: 'Instagram',
    twitter: 'Twitter',
    x: 'X',
    youtube: 'YouTube',
    github: 'GitHub',
    discord: 'Discord',
    threads: 'Threads',
    tiktok: 'TikTok',
    whatsapp: 'WhatsApp',
  };
  const key = String(platform || '').toLowerCase();
  return labels[key] || 'Social link';
}

const Footer = () => {
  const [data, setData] = useState(defaultFooter);

  useEffect(() => {
    const fetchFooter = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/footer`);
        if (res.data?.brandName !== undefined) {
          setData({
            ...defaultFooter,
            ...res.data,
            focusItems: Array.isArray(res.data.focusItems)
              ? res.data.focusItems
              : defaultFooter.focusItems,
            socialLinks: normalizeSocialLinksFromApi(res.data),
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(err);
      }
    };

    fetchFooter();
  }, []);

  const pillars = Array.isArray(data.focusItems) ? data.focusItems : defaultFooter.focusItems;

  const visibleSocialLinks = (data.socialLinks || []).filter((item) =>
    hasDisplayableSocialUrl(item.url)
  );

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer-inner">
          <div className="footer-columns">
            <div className="footer-col footer-firm">
              <div className="footer-logo">
                <span className="brand">{data.brandName}</span>
              </div>
              <p className="firm-description">{data.firmDescription}</p>
            </div>

            <div className="footer-col footer-focus">
              <h3 className="footer-heading">{data.focusHeading}</h3>
              <ul className="footer-pillars">
                {pillars.filter(Boolean).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="footer-col footer-contact">
              <h3 className="footer-heading">{data.contactHeading}</h3>
              <div className="contact-item">
                <MapPin size={18} className="contact-icon" />
                <span>{data.address}</span>
              </div>
              <div className="contact-item">
                <Phone size={18} className="contact-icon" />
                <span>{data.phone}</span>
              </div>
              <div className="contact-item">
                <Mail size={18} className="contact-icon" />
                <span>{data.email}</span>
              </div>
            </div>
          </div>

          <div className="footer-bottom">
            <p className="footer-copyright">{data.copyrightText}</p>
            {visibleSocialLinks.length > 0 && (
              <div className="social-icons">
                {visibleSocialLinks.map((item, index) => {
                  const iconName = lucideIconForFooterPlatform(item.platform);
                  return (
                    <a
                      key={`${item.platform}-${index}`}
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={socialAriaLabel(item.platform)}
                      className="social-link"
                    >
                      <DynamicIcon
                        name={iconName}
                        fallback={Link}
                        size={20}
                        strokeWidth={2}
                      />
                    </a>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
